import { createService } from "./createService"
import { SERVICE_INSTANCES } from "./serviceInstances"

const service = createService(SERVICE_INSTANCES.disciplinary)

export const DISCIPLINARY_INSTANCE = service.instanceKey

export const DISCIPLINARY_ENDPOINTS = {
  // Admin — violations
  SELECT_VIOLATIONS: "/admin/disciplinary/selectviolations",
  SELECT_VIOLATION_TYPES: "/admin/disciplinary/selectviolationtypes",
  SELECT_VIOLATION_TRIGGERS: "/admin/disciplinary/selectviolationtriggers",
  REVIEW: "/admin/disciplinary/review",
  GET_PERSONS_FOR_VIOLATION: "/admin/disciplinary/getpersonsforviolation",
  GET_EMPLOYEE_VIOLATION_TYPES: "/admin/disciplinary/getemployeeviolationtypes",
  GET_VIOLATION_ACTIONS: "/admin/disciplinary/getviolationactions",
  VIEW_VIOLATIONS: "/admin/disciplinary/view/violations",
  CHECK_VIOLATION_POLICY: "/admin/disciplinary/checkviolationpolicy",
  CHECK_VIOLATION_TYPE: "/admin/disciplinary/checkviolationtype",
  ADD_VIOLATION_POLICY: "/admin/disciplinary/add_violationpolicy",
  UPDATE_VIOLATION_POLICY: "/admin/disciplinary/update_violationpolicy",
  DELETE_VIOLATION_POLICY: "/admin/disciplinary/delete_violationpolicy",
  PROCESS_TRIGGER: "/admin/disciplinary/process_trigger",
  REVIEW_TRIGGER: "/admin/disciplinary/review_trigger",
  DELETE_TRIGGER: "/admin/disciplinary/delete_trigger",
  ADD_VIOLATION_TYPE: "/admin/disciplinary/add_violationtype",
  UPDATE_VIOLATION_TYPE: "/admin/disciplinary/update_violationtype",
  DELETE_VIOLATION_TYPE: "/admin/disciplinary/delete_violationtype",
  // Admin — suspensions
  SELECT_SUSPENSIONS: "/admin/disciplinary/selectsuspensions",
  SELECT_SUSPENSION_SCHEDULES: "/admin/disciplinary/selectsuspensionschedules",
  ADD_SUSPENSION: "/admin/disciplinary/add_suspension",
  DELETE_SUSPENSION: "/admin/disciplinary/delete_suspension",
  UPDATE_EMPLOYEE_SUSPENSION: "/admin/disciplinary/update_employeesuspension",
  IMPORT_SUSPENSIONS: "/admin/disciplinary/import_suspensions",
  DOWNLOAD_BULK_SUSPENSION_FORM:
    "/admin/disciplinary/view/download_bulk_suspension_upload_form",
  // Admin — record violation on behalf of employee
  ADMIN_ADD_EMPLOYEE_VIOLATION: "/admin/disciplinary/add_employeeviolation",
  ADMIN_UPLOAD_VIOLATION_FILE: "/admin/disciplinary/upload_violation_file",
  // Employee
  SELECT_RECEIVED_VIOLATIONS: "/employee/disciplinary/selectreceivedviolations",
  SELECT_RECORDED_VIOLATIONS: "/employee/disciplinary/selectrecordedviolations",
  ADD_EMPLOYEE_VIOLATION: "/employee/disciplinary/add_employeeviolation",
  UPLOAD_VIOLATION_FILE: "/employee/disciplinary/upload_violation_file",
  // Shared (approval / chat)
  VIEW_VIOLATION: "/disciplinary/view/violation",
  SELECT_APPROVAL_VIOLATIONS: "/disciplinary/selectviolations",
  SELECT_APPROVAL_SUSPENSIONS: "/disciplinary/selectsuspensions",
  ADD_VIOLATION_COMMENT: "/disciplinary/add_employeeviolation_comment",
  EDIT_VIOLATION_COMMENT: "/disciplinary/edit_employeeviolation_comment",
  DELETE_VIOLATION_COMMENT: "/disciplinary/delete_employeeviolation_comment",
} as const

export const disciplinaryService = {
  instanceKey: service.instanceKey,

  // Admin — violations listing
  selectViolations: (statusId: string | number, params?: Record<string, any>) =>
    service.get(DISCIPLINARY_ENDPOINTS.SELECT_VIOLATIONS, {
      statusId,
      ...params,
    }),
  selectViolationTypes: (params?: Record<string, any>) =>
    service.get(DISCIPLINARY_ENDPOINTS.SELECT_VIOLATION_TYPES, params),
  selectViolationTriggers: (
    statusId: string | number,
    params?: Record<string, any>
  ) =>
    service.get(DISCIPLINARY_ENDPOINTS.SELECT_VIOLATION_TRIGGERS, {
      statusId,
      ...params,
    }),

  // Admin — violation actions
  reviewViolation: (id: string | number, actionType?: string, note?: string) =>
    service.post(
      `${DISCIPLINARY_ENDPOINTS.REVIEW}?id=${id}${
        actionType ? `&actionType=${actionType}` : ""
      }${note ? `&note=${note}` : ""}`
    ),
  getPersonsForViolation: (
    all: boolean | string,
    params?: Record<string, any>
  ) =>
    service.get(DISCIPLINARY_ENDPOINTS.GET_PERSONS_FOR_VIOLATION, {
      all,
      ...params,
    }),
  getEmployeeViolationTypes: (
    forpolicy: boolean | string,
    params?: Record<string, any>
  ) =>
    service.get(DISCIPLINARY_ENDPOINTS.GET_EMPLOYEE_VIOLATION_TYPES, {
      forpolicy,
      ...params,
    }),
  getViolationActions: () =>
    service.get(DISCIPLINARY_ENDPOINTS.GET_VIOLATION_ACTIONS),

  // Admin — violation policies
  viewViolations: (id: string, x?: string) =>
    service.get(DISCIPLINARY_ENDPOINTS.VIEW_VIOLATIONS, x ? { id, x } : { id }),
  checkViolationPolicy: (
    description: string,
    violation_policy_id: string | number = -1
  ) =>
    service.get(DISCIPLINARY_ENDPOINTS.CHECK_VIOLATION_POLICY, {
      description,
      violation_policy_id,
    }),
  checkViolationType: (description: string) =>
    service.get(DISCIPLINARY_ENDPOINTS.CHECK_VIOLATION_TYPE, { description }),
  addViolationPolicy: (body: Record<string, any>) =>
    service.post(DISCIPLINARY_ENDPOINTS.ADD_VIOLATION_POLICY, body),
  updateViolationPolicy: (id: string | number, body: Record<string, any>) =>
    service.post(
      `${DISCIPLINARY_ENDPOINTS.UPDATE_VIOLATION_POLICY}?id=${id}`,
      body
    ),
  deleteViolationPolicy: (id: string | number) =>
    service.post(`${DISCIPLINARY_ENDPOINTS.DELETE_VIOLATION_POLICY}?id=${id}`),
  processTrigger: (id: string | number) =>
    service.post(`${DISCIPLINARY_ENDPOINTS.PROCESS_TRIGGER}?id=${id}`),
  reviewTrigger: (id: string | number, body: Record<string, any>) =>
    service.post(`${DISCIPLINARY_ENDPOINTS.REVIEW_TRIGGER}?id=${id}`, body),
  deleteTrigger: (id: string | number) =>
    service.post(`${DISCIPLINARY_ENDPOINTS.DELETE_TRIGGER}?id=${id}`),

  // Admin — violation types
  addViolationType: (body: Record<string, any>) =>
    service.post(DISCIPLINARY_ENDPOINTS.ADD_VIOLATION_TYPE, body),
  updateViolationType: (body: Record<string, any>) =>
    service.post(`${DISCIPLINARY_ENDPOINTS.UPDATE_VIOLATION_TYPE}`, body),
  deleteViolationType: (id: string | number) =>
    service.post(`${DISCIPLINARY_ENDPOINTS.DELETE_VIOLATION_TYPE}?id=${id}`),

  // Admin — suspensions
  selectSuspensions: (
    statusId: string | number,
    params?: Record<string, any>
  ) =>
    service.get(DISCIPLINARY_ENDPOINTS.SELECT_SUSPENSIONS, {
      statusId,
      ...params,
    }),
  selectSuspensionSchedules: (
    statusId?: string | number,
    params?: Record<string, any>
  ) =>
    service.get(DISCIPLINARY_ENDPOINTS.SELECT_SUSPENSION_SCHEDULES, {
      ...(statusId !== undefined ? { statusId } : {}),
      ...params,
    }),
  addSuspension: (body: Record<string, any>) =>
    service.post(DISCIPLINARY_ENDPOINTS.ADD_SUSPENSION, body),
  deleteSuspension: (id: string | number) =>
    service.post(`${DISCIPLINARY_ENDPOINTS.DELETE_SUSPENSION}?id=${id}`),
  updateEmployeeSuspension: (id: string | number, body: Record<string, any>) =>
    service.post(
      `${DISCIPLINARY_ENDPOINTS.UPDATE_EMPLOYEE_SUSPENSION}?id=${id}`,
      body
    ),
  importSuspensions: (formData: FormData) =>
    service.upload(DISCIPLINARY_ENDPOINTS.IMPORT_SUSPENSIONS, formData),

  // Admin — record violation on behalf of employee
  adminAddEmployeeViolation: (body: Record<string, any>) =>
    service.post(DISCIPLINARY_ENDPOINTS.ADMIN_ADD_EMPLOYEE_VIOLATION, body),
  adminUploadViolationFile: (id: string | number, formData: FormData) =>
    service.upload(
      `${DISCIPLINARY_ENDPOINTS.ADMIN_UPLOAD_VIOLATION_FILE}?id=${id}`,
      formData
    ),

  // Employee
  selectReceivedViolations: (params?: Record<string, any>) =>
    service.get(DISCIPLINARY_ENDPOINTS.SELECT_RECEIVED_VIOLATIONS, params),
  selectRecordedViolations: (params?: Record<string, any>) =>
    service.get(DISCIPLINARY_ENDPOINTS.SELECT_RECORDED_VIOLATIONS, params),
  addEmployeeViolation: (body: Record<string, any>) =>
    service.post(DISCIPLINARY_ENDPOINTS.ADD_EMPLOYEE_VIOLATION, body),
  uploadViolationFile: (id: string | number, formData: FormData) =>
    service.upload(
      `${DISCIPLINARY_ENDPOINTS.UPLOAD_VIOLATION_FILE}?id=${id}`,
      formData
    ),

  // Shared
  viewViolation: (id: string | number) =>
    service.get(DISCIPLINARY_ENDPOINTS.VIEW_VIOLATION, { id }),
  selectApprovalViolations: (params?: Record<string, any>) =>
    service.get(DISCIPLINARY_ENDPOINTS.SELECT_APPROVAL_VIOLATIONS, params),
  selectApprovalSuspensions: (params?: Record<string, any>) =>
    service.get(DISCIPLINARY_ENDPOINTS.SELECT_APPROVAL_SUSPENSIONS, params),
  addViolationComment: (body: Record<string, any>) =>
    service.post(DISCIPLINARY_ENDPOINTS.ADD_VIOLATION_COMMENT, body),
  editViolationComment: (body: Record<string, any>) =>
    service.post(DISCIPLINARY_ENDPOINTS.EDIT_VIOLATION_COMMENT, body),
  deleteViolationComment: (body: Record<string, any>) =>
    service.post(DISCIPLINARY_ENDPOINTS.DELETE_VIOLATION_COMMENT, body),
}
